import React, { useEffect, useEffect as useEffectAuth, useRef, useState as useStateAuth } from "react";
import { Api } from "./api";
import { Brand, Field, Icon, Logo } from "./components";

// Auth screens: login + community-signup + homeowner-signup, tabbed.
// Wired to the Flask backend at window.APP_CONFIG.apiBase.

// True when the SPA is running inside a Capacitor native shell
// (iOS or Android). Used to slim the auth screen for mobile: hide
// HOA-onboarding (a multi-step manager flow better-suited to
// desktop) and swap the marketing pricing CTA for an
// external-browser link to the web site.
function _isCapacitorNative() {
  try {
    const cap = window.Capacitor;
    if (!cap) return false;
    const platform =
      (typeof cap.getPlatform === "function" && cap.getPlatform()) ||
      cap.platform;
    return platform === "ios" || platform === "android";
  } catch (_) {
    return false;
  }
}

// Web-only "Download on the App Store" badge, shown under the OAuth
// buttons on every auth tab. Hidden inside the native Capacitor shells:
// it's nonsensical there, and an in-app App Store link would trip
// Apple's anti-steering rules. Official Apple badge artwork lives at
// frontend/public/app-store-badge.svg (served same-origin → CSP-clean).
function AppStoreBadge() {
  if (_isCapacitorNative()) return null;
  return (
    <a
      href="https://apps.apple.com/us/app/communitycompassai/id6772768068"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Download Community Compass on the App Store"
      style={{ display: "block", width: "fit-content", margin: "2px auto 8px" }}
    >
      <img
        src="/app-store-badge.svg"
        alt="Download on the App Store"
        height={44}
        style={{ display: "block", height: 44, width: "auto" }}
      />
    </a>
  );
}

function slugify(name) {
  return (name || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// Password policy mirror — must match `_password_policy_error()` in
// app/auth/routes.py. Without this match the UI passes its checks at
// 8 chars and the backend rejects the request as too short / too
// weak, surfacing a confusing post-submit error instead of inline
// guidance (issue #210).
const PASSWORD_MIN_LEN = 12;
const PASSWORD_HINT = (
  "At least 12 characters with a letter and a digit or symbol."
);
// Public so settings.jsx (change-password form) can mirror the same
// rule + copy without redefining it.
export { PASSWORD_MIN_LEN, PASSWORD_HINT };
function passwordPolicyError(pwd) {
  if (typeof pwd !== "string" || pwd.length < PASSWORD_MIN_LEN) {
    return `Password must be at least ${PASSWORD_MIN_LEN} characters.`;
  }
  if (!/[A-Za-z]/.test(pwd)) {
    return "Password must include at least one letter.";
  }
  if (!/[^A-Za-z]/.test(pwd)) {
    return "Password must include at least one digit or symbol.";
  }
  return null;
}
export { passwordPolicyError };

// 50 states + DC. Used by HOA onboarding and homeowner signup so two
// communities with the same name in different states stay distinct.
const US_STATES = [
  ["AL","Alabama"],["AK","Alaska"],["AZ","Arizona"],["AR","Arkansas"],
  ["CA","California"],["CO","Colorado"],["CT","Connecticut"],["DE","Delaware"],
  ["DC","District of Columbia"],["FL","Florida"],["GA","Georgia"],["HI","Hawaii"],
  ["ID","Idaho"],["IL","Illinois"],["IN","Indiana"],["IA","Iowa"],
  ["KS","Kansas"],["KY","Kentucky"],["LA","Louisiana"],["ME","Maine"],
  ["MD","Maryland"],["MA","Massachusetts"],["MI","Michigan"],["MN","Minnesota"],
  ["MS","Mississippi"],["MO","Missouri"],["MT","Montana"],["NE","Nebraska"],
  ["NV","Nevada"],["NH","New Hampshire"],["NJ","New Jersey"],["NM","New Mexico"],
  ["NY","New York"],["NC","North Carolina"],["ND","North Dakota"],["OH","Ohio"],
  ["OK","Oklahoma"],["OR","Oregon"],["PA","Pennsylvania"],["RI","Rhode Island"],
  ["SC","South Carolina"],["SD","South Dakota"],["TN","Tennessee"],["TX","Texas"],
  ["UT","Utah"],["VT","Vermont"],["VA","Virginia"],["WA","Washington"],
  ["WV","West Virginia"],["WI","Wisconsin"],["WY","Wyoming"],
];
// Expose for any other component that needs the same list.
export { US_STATES };
// Cloudflare Turnstile CAPTCHA widget (issue #175). Renders only
// when `siteKey` is non-empty; calls `onToken(token)` on solve,
// `onToken("")` on expire or error so a stale token doesn't get
// submitted. The Cloudflare loader (script tag in index.html) sets
// window.turnstile asynchronously, so we poll briefly until it's
// available before rendering. Cleanup removes the widget on unmount
// so a tab switch doesn't leak DOM nodes.
function TurnstileWidget({ siteKey, onToken }) {
  const containerRef = React.useRef(null);
  const widgetIdRef = React.useRef(null);
  React.useEffect(() => {
    if (!siteKey || !containerRef.current) return;
    let cancelled = false;
    let pollHandle = null;

    const tryRender = () => {
      if (cancelled) return;
      const t = window.turnstile;
      if (!t || typeof t.render !== "function") {
        // Loader script hasn't finished yet; check again shortly.
        pollHandle = setTimeout(tryRender, 100);
        return;
      }
      try {
        widgetIdRef.current = t.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => onToken(token || ""),
          "expired-callback": () => onToken(""),
          "error-callback": () => onToken(""),
          "timeout-callback": () => onToken(""),
        });
      } catch (err) {
        // Bad site key, invalid container — log and leave the widget
        // unrendered. Form falls back to "please complete CAPTCHA"
        // which the user can fix by reloading.
        console.warn("Turnstile render failed:", err);
      }
    };
    tryRender();

    return () => {
      cancelled = true;
      if (pollHandle) clearTimeout(pollHandle);
      if (window.turnstile && widgetIdRef.current != null) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (_) { /* ignore */ }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, onToken]);

  if (!siteKey) return null;
  return <div ref={containerRef} style={{ marginBottom: 8 }} />;
}


function AuthScreen({ onLogin, onSeePricing, initialTab, sessionExpired, onSessionExpiredAck }) {
  // tab values:
  //   login | community | homeowner       — normal sign-in / signup flows
  //   forgot                              — "enter email to get reset link"
  //   forgot-sent                         — success state after request
  //   reset                               — "set new password" (came from email link)
  //   reset-done                          — success state after reset
  //
  // Tab precedence on mount:
  //   1. URL tokens (invite_token / reset_token) always win — those are
  //      deep-links from emails and must land on the matching form
  //   2. initialTab from App (set when closing the public PricingPage
  //      via "Onboard our HOA" → "community" tab)
  //   3. default to "login"
  const [tab, setTab] = useStateAuth(() => {
    if (typeof window !== "undefined") {
      try {
        const u = new URL(window.location.href);
        if (u.searchParams.get("invite_token")) return "accept-invite";
        if (u.searchParams.get("reset_token")) return "reset";
        if (u.searchParams.get("verify_token")) return "verify-result";
      } catch (_) { /* ignore */ }
    }
    if (initialTab === "community" || initialTab === "homeowner" || initialTab === "login") {
      // HOA onboarding is desktop-only; on the native mobile app
      // bounce the request to login (matches the hidden tab button).
      if (initialTab === "community" && _isCapacitorNative()) return "login";
      return initialTab;
    }
    return "login";
  });

  // Defensive guard: if some other code path (deep link, restored
  // route, future feature) sets tab to "community" while we're in
  // the native app shell, bounce back to login so the user doesn't
  // see an "Onboard your HOA" form with no tab affordance to leave.
  useEffectAuth(() => {
    if (tab === "community" && _isCapacitorNative()) {
      setTab("login");
    }
  }, [tab]);

  // Verify-token from email link: status of the POST. "pending"
  // while in flight, "success" / "expired" / "error" after.
  const [verifyResult, setVerifyResult] = useStateAuth(null);

  // Login fields. Initialized from window.APP_CONFIG.demoPrefill, which
  // the inline script in index.html populates BEFORE React/Babel boot.
  // That script also strips the source `?prefill_email=` /
  // `?prefill_password=` params from the URL synchronously — so by
  // the time AuthScreen mounts, the URL bar already shows the clean
  // address. This closes the one-render window where the password
  // was visible in the URL between mount and the URL-stripping
  // useEffect. CODE_REVIEW P2 L264 / issue #155.
  const [loginEmail, setLoginEmail] = useStateAuth(() => {
    if (typeof window === "undefined") return "";
    return (window.APP_CONFIG && window.APP_CONFIG.demoPrefill && window.APP_CONFIG.demoPrefill.email) || "";
  });
  const [loginPassword, setLoginPassword] = useStateAuth(() => {
    if (typeof window === "undefined") return "";
    return (window.APP_CONFIG && window.APP_CONFIG.demoPrefill && window.APP_CONFIG.demoPrefill.password) || "";
  });

  // When the email+password matches accounts in multiple communities, the
  // backend returns the list and we prompt the user to pick.
  const [pickerCommunities, setPickerCommunities] = useStateAuth(null);

  // Forgot / reset state
  const [forgotEmail, setForgotEmail] = useStateAuth("");
  const [resetToken, setResetTokenState] = useStateAuth(() => {
    if (typeof window === "undefined") return "";
    try {
      return new URL(window.location.href).searchParams.get("reset_token") || "";
    } catch (_) { return ""; }
  });
  const [resetPassword, setResetPassword] = useStateAuth("");
  const [resetPasswordConfirm, setResetPasswordConfirm] = useStateAuth("");

  // Household invite acceptance. Token comes from the link the
  // primary copied out of Settings → Household. We support two
  // modes: a brand-new account (needs a password) or an existing
  // account being added as a secondary (needs the existing password
  // for verification so a stolen invite link can't take over an
  // account). The form picks between them based on what the user
  // tells us.
  const [inviteToken] = useStateAuth(() => {
    if (typeof window === "undefined") return "";
    try {
      return new URL(window.location.href).searchParams.get("invite_token") || "";
    } catch (_) { return ""; }
  });
  const [inviteFullName, setInviteFullName] = useStateAuth("");
  const [invitePassword, setInvitePassword] = useStateAuth("");
  const [inviteExistingPassword, setInviteExistingPassword] = useStateAuth("");
  const [inviteHasAccount, setInviteHasAccount] = useStateAuth(false);

  // Homeowner signup fields. hoState picks the state; hoCommunitySlug
  // gets set from the dropdown of communities in that state (so we
  // avoid the slug-collision bug where two communities share a name).
  // hoCommunityManual is a fallback free-text slug for users whose
  // community hasn't filled in its state yet.
  const [hoState, setHoState] = useStateAuth("");
  const [hoCommunitySlug, setHoCommunitySlug] = useStateAuth("");
  const [hoCommunityManual, setHoCommunityManual] = useStateAuth("");
  const [hoUseManual, setHoUseManual] = useStateAuth(false);
  const [hoCommunities, setHoCommunities] = useStateAuth({ loading: false, list: [] });
  const [hoFirst, setHoFirst] = useStateAuth("");
  const [hoLast, setHoLast] = useStateAuth("");
  const [hoEmail, setHoEmail] = useStateAuth("");
  const [hoUnit, setHoUnit] = useStateAuth("");
  const [hoPassword, setHoPassword] = useStateAuth("");

  // Fetch communities whenever the state changes (or on first mount
  // of the tab if state is already set).
  useEffectAuth(() => {
    if (!hoState) {
      setHoCommunities({ loading: false, list: [] });
      setHoCommunitySlug("");
      return;
    }
    let cancelled = false;
    setHoCommunities({ loading: true, list: [] });
    Api.communitiesLookup({ state: hoState })
      .then((list) => {
        if (cancelled) return;
        setHoCommunities({ loading: false, list: list || [] });
        // Reset selection if it no longer matches the new list.
        if (hoCommunitySlug && !(list || []).some((c) => c.slug === hoCommunitySlug)) {
          setHoCommunitySlug("");
        }
      })
      .catch(() => { if (!cancelled) setHoCommunities({ loading: false, list: [] }); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoState]);

  // Community onboarding fields
  const [cmName, setCmName] = useStateAuth("");
  const [cmUnits, setCmUnits] = useStateAuth("");
  const [cmState, setCmState] = useStateAuth("");
  const [cmCompany, setCmCompany] = useStateAuth("no");
  // PMC name — only shown / used when cmCompany === "new". The
  // backend uses this as PropertyManagementCompany.name.
  const [cmCompanyName, setCmCompanyName] = useStateAuth("");
  const [cmYourName, setCmYourName] = useStateAuth("");
  const [cmEmail, setCmEmail] = useStateAuth("");
  const [cmPassword, setCmPassword] = useStateAuth("");
  // Cloudflare Turnstile CAPTCHA token (issue #175). Populated by
  // the widget on solve; cleared after submit so a second signup
  // attempt in the same session has to re-solve. Site key is
  // fetched from /api/auth/public-config on mount.
  const [cmCaptchaToken, setCmCaptchaToken] = useStateAuth("");
  // Tri-state:
  //   null  = haven't successfully fetched yet (still loading OR
  //           retrying). Keep submit disabled and show a loading
  //           placeholder.
  //   ""    = backend answered "no Turnstile configured here"
  //           (dev / unconfigured deploy). Submit enabled, no
  //           widget. Backend won't enforce CAPTCHA either.
  //   "..." = real site key. Show widget; submit disabled until
  //           the user solves the challenge.
  const [turnstileSiteKey, setTurnstileSiteKey] = useStateAuth(null);
  // Tracks whether retries are exhausted so the UI can surface a
  // "couldn't load — refresh" message + manual retry button. We DO
  // NOT collapse this into setTurnstileSiteKey("") because that
  // signal means "Turnstile not configured" and would falsely
  // enable submit while the backend still enforces the gate.
  // (Codex P2 round 3 on PR #192.)
  const [turnstileLoadFailed, setTurnstileLoadFailed] = useStateAuth(false);
  // Bumping this triggers a re-fetch via the useEffect dependency.
  const [turnstileFetchAttempt, setTurnstileFetchAttempt] = useStateAuth(0);
  useEffectAuth(() => {
    // Fetch the public Turnstile site key once on mount (and again
    // whenever the manual "try again" button bumps the attempt
    // counter). If the first network call fails (blip, 429), retry
    // automatically up to 2 attempts with a short delay. If still
    // failing, leave turnstileSiteKey as `null` and set the
    // loadFailed flag so the UI can show a refresh CTA.
    let cancelled = false;
    let retryHandle = null;
    setTurnstileLoadFailed(false);

    const fetchKey = (attempt) => {
      Api.publicConfig().then((cfg) => {
        if (cancelled) return;
        // Distinguish "backend said empty" from "fetch failed."
        // Api.publicConfig only caches on SUCCESS, so a populated
        // cache (including "") is a real answer.
        const cached = window.APP_CONFIG && window.APP_CONFIG.turnstileSiteKey;
        if (cached === "" || (typeof cached === "string" && cached.length > 0)) {
          setTurnstileSiteKey(cached);
          return;
        }
        if (attempt < 2) {
          retryHandle = setTimeout(() => fetchKey(attempt + 1), 1500);
        } else {
          setTurnstileLoadFailed(true);
        }
      }).catch(() => {
        if (cancelled) return;
        if (attempt < 2) {
          retryHandle = setTimeout(() => fetchKey(attempt + 1), 1500);
        } else {
          setTurnstileLoadFailed(true);
        }
      });
    };

    fetchKey(1);
    return () => {
      cancelled = true;
      if (retryHandle) clearTimeout(retryHandle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turnstileFetchAttempt]);

  // Email-address shown on the "check your inbox" verify-sent
  // screen — captured from the signup response so the SPA can
  // render "we sent a link to bob@foo.com".
  const [verifyEmailAddress, setVerifyEmailAddress] = useStateAuth("");

  // Shared submission state
  const [busy, setBusy] = useStateAuth(false);
  const [error, setError] = useStateAuth("");

  // DemoMode auto-sign-in: when the local panel opens this tab with
  // ?prefill_email=...&prefill_password=..., the email + password
  // states are already populated from the URL above. This effect:
  //   (1) strips the params from the URL so they don't sit in
  //       browser history or get accidentally shared with a screenshot
  //   (2) auto-submits the login IF the email matches the demo
  //       pattern (*+demo@*.compass.local). Real accounts never
  //       auto-submit, even with valid prefill params — the user has
  //       to click Sign In manually. That keeps the auto-submit
  //       behavior locked to throwaway demo accounts only.
  // Email-verification token consumer: if the URL carries
  // ?verify_token=..., POST it to /verify-email immediately and
  // surface the result on the verify-result tab. Codex P1 on PR
  // #142 round 8 — without this, email links did nothing and new
  // accounts stayed unverifiable.
  useEffectAuth(() => {
    if (typeof window === "undefined") return;
    let token;
    try {
      const u = new URL(window.location.href);
      token = u.searchParams.get("verify_token");
    } catch (_) {
      return;
    }
    if (!token) return;
    // Strip the token from the URL before doing anything else so a
    // refresh doesn't try to reuse a one-shot token (the backend
    // returns invalid_or_expired on the second attempt anyway, but
    // cleaner state for the user).
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete("verify_token");
      window.history.replaceState({}, "", u.toString());
    } catch (_) { /* ignore */ }
    setVerifyResult("pending");
    Api.verifyEmail({ token })
      .then(() => { setVerifyResult("success"); })
      .catch((err) => {
        if (err && err.code === "invalid_or_expired") {
          setVerifyResult("expired");
        } else {
          setVerifyResult("error");
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffectAuth(() => {
    if (typeof window === "undefined") return;
    // The URL was already stripped by the inline script in
    // index.html before React mounted (issue #155). What this effect
    // still owns is: consume-once of the prefill stash + auto-submit
    // when the values look like a demo account. The consume-once
    // step is what gates auto-submit (was: a `stripped` boolean
    // computed from the URL).
    const prefill = window.APP_CONFIG && window.APP_CONFIG.demoPrefill;
    if (prefill) {
      try { delete window.APP_CONFIG.demoPrefill; }
      catch (_) { window.APP_CONFIG.demoPrefill = null; }
    }

    const isDemoAccount = /^[^@\s]+\+demo@[^@\s]+\.compass\.local$/i.test(loginEmail || "");
    if (prefill && loginEmail && loginPassword && isDemoAccount && tab === "login") {
      // Small delay so a recording shows the prefilled form briefly
      // before the submit fires — looks intentional rather than
      // mysterious.
      const t = setTimeout(() => submitLogin(), 300);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finish = (data) => {
    // Backend may respond `email_verification_required: true` on
    // signup paths instead of issuing a JWT (CODE_REVIEW P2 L228).
    // Surface the check-your-email tab rather than trying to log in
    // — no token to seed, and trying to call protected APIs would
    // 401 (or worse, 403 email_verification_required on next login).
    if (data && data.email_verification_required) {
      setVerifyEmailAddress(data.user?.email || "");
      setTab("verify-sent");
      return;
    }
    const role = Api.uiRole(data.user?.role);
    onLogin(role, data.user, data.token);
  };

  const submitLogin = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    setError("");
    if (!loginEmail || !loginPassword) {
      setError("Email and password are required.");
      return;
    }
    setBusy(true);
    try {
      const data = await Api.login({
        email: loginEmail,
        password: loginPassword,
      });
      if (data?.multi_community) {
        setPickerCommunities(data.communities || []);
        return;
      }
      finish(data);
    } catch (err) {
      // Unverified-account 403 routes to the verify-sent screen
      // (with resend) rather than leaving the user stuck on a
      // password error they can't act on. CODE_REVIEW codex P1
      // on PR #142 round 8.
      if (err && err.code === "email_verification_required") {
        setVerifyEmailAddress(loginEmail);
        setTab("verify-sent");
        return;
      }
      setError(err.message || "Sign-in failed.");
    } finally {
      setBusy(false);
    }
  };

  const pickCommunity = async (community_id) => {
    if (busy) return;
    setError("");
    setBusy(true);
    try {
      const data = await Api.login({
        email: loginEmail,
        password: loginPassword,
        community_id,
      });
      // Defensive: server should return a token at this point. If it
      // somehow returns multi again, just re-prompt rather than crash.
      if (data?.multi_community) {
        setPickerCommunities(data.communities || []);
        return;
      }
      setPickerCommunities(null);
      finish(data);
    } catch (err) {
      setError(err.message || "Sign-in failed.");
    } finally {
      setBusy(false);
    }
  };

  const submitForgot = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    setError("");
    const email = forgotEmail.trim();
    if (!email) {
      setError("Email is required.");
      return;
    }
    setBusy(true);
    try {
      await Api.passwordResetRequest({ email });
      setTab("forgot-sent");
    } catch (err) {
      setError(err.message || "Couldn't send the reset email.");
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    setError("");
    if (!resetToken) {
      setError("Missing reset token. Please use the link from your email.");
      return;
    }
    const _pwErr = passwordPolicyError(resetPassword);
    if (_pwErr) { setError(_pwErr); return; }
    if (resetPassword !== resetPasswordConfirm) {
      setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await Api.passwordResetConfirm({ token: resetToken, password: resetPassword });
      // Clean the token off the URL so a refresh doesn't try to reuse it.
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete("reset_token");
        window.history.replaceState({}, "", url.toString());
      } catch (_) { /* ignore */ }
      setResetTokenState("");
      setResetPassword("");
      setResetPasswordConfirm("");
      setTab("reset-done");
    } catch (err) {
      setError(err.message || "Couldn't reset your password.");
    } finally {
      setBusy(false);
    }
  };

  const submitHomeowner = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    setError("");
    const slug = hoUseManual ? slugify(hoCommunityManual) : hoCommunitySlug;
    if (!slug || !hoEmail || !hoPassword) {
      setError("Community, email, and password are required.");
      return;
    }
    const _pwErr = passwordPolicyError(hoPassword);
    if (_pwErr) { setError(_pwErr); return; }
    setBusy(true);
    try {
      const data = await Api.registerHomeowner({
        community_slug: slug,
        email: hoEmail,
        password: hoPassword,
        full_name: `${hoFirst} ${hoLast}`.trim(),
      });
      finish(data);
    } catch (err) {
      setError(err.message || "Sign-up failed.");
    } finally {
      setBusy(false);
    }
  };

  const submitCommunity = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    setError("");
    if (!cmName || !cmEmail || !cmPassword) {
      setError("Community name, email, and password are required.");
      return;
    }
    if (!cmState) {
      setError("Please pick a state.");
      return;
    }
    const _pwErr = passwordPolicyError(cmPassword);
    if (_pwErr) { setError(_pwErr); return; }
    // CAPTCHA required (issue #175) — but only when a Turnstile site
    // key is configured. Dev / unconfigured environments skip the
    // gate; production refuses to boot without the key so this
    // branch only relaxes signup in legitimate dev contexts.
    if (turnstileSiteKey && !cmCaptchaToken) {
      setError("Please complete the CAPTCHA before continuing.");
      return;
    }
    setBusy(true);
    try {
      // Map the company picker into the wire format: "new" + a name
      // creates the PMC; "no" leaves us as a single-HOA account.
      // "yes" (join an existing PMC) isn't supported in the self-serve
      // flow yet — that path needs a verification step so a new
      // manager can't attach themselves to an arbitrary PMC; treat
      // it as "no" here and route those users to a follow-up email.
      const companySetup = cmCompany === "new" ? "new" : "none";
      if (companySetup === "new" && !cmCompanyName.trim()) {
        setError("Company name is required when setting up a new PMC.");
        setBusy(false);
        return;
      }
      let data;
      try {
        data = await Api.registerCommunity({
          community_name: cmName,
          email: cmEmail,
          password: cmPassword,
          full_name: cmYourName,
          state: cmState,
          unit_count: cmUnits ? Number(cmUnits) : null,
          company_setup: companySetup,
          company_name: companySetup === "new" ? cmCompanyName.trim() : undefined,
          cf_turnstile_token: cmCaptchaToken || undefined,
        });
      } finally {
        // Turnstile tokens are single-use — reset on EVERY return
        // path, success or failure. Without this, a backend 409
        // (email in use) or any post-CAPTCHA validation error would
        // leave the spent token in state, and the next submit would
        // get captcha_failed until the user refreshed. Codex P2 on
        // PR #192.
        setCmCaptchaToken("");
        try { window.turnstile && window.turnstile.reset(); } catch (_) { /* ignore */ }
      }
      finish(data);
    } catch (err) {
      setError(err.message || "Sign-up failed.");
    } finally {
      setBusy(false);
    }
  };

  const switchTab = (next) => {
    setError("");
    setTab(next);
  };

  const submitInviteAccept = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    setError("");
    if (!inviteToken) {
      setError("Missing invite token. Please use the link from your household primary.");
      return;
    }
    if (inviteHasAccount) {
      if (!inviteExistingPassword) {
        setError("Re-enter your current account password to confirm.");
        return;
      }
    } else {
      const _pwErr = passwordPolicyError(invitePassword);
      if (_pwErr) { setError(_pwErr); return; }
    }
    setBusy(true);
    try {
      const data = await Api.acceptHouseholdInvite({
        token: inviteToken,
        password: inviteHasAccount ? undefined : invitePassword,
        full_name: inviteFullName || undefined,
        existing_account_password: inviteHasAccount ? inviteExistingPassword : undefined,
      });
      // Clean the token off the URL so a refresh doesn't 409 with
      // "already_accepted".
      try {
        const u = new URL(window.location.href);
        u.searchParams.delete("invite_token");
        window.history.replaceState({}, "", u.toString());
      } catch (_) { /* ignore */ }
      finish(data);
    } catch (err) {
      // If the backend tells us the email already has an account,
      // flip the form into "existing account" mode automatically.
      if (err && err.code === "existing_account_password_required") {
        setInviteHasAccount(true);
        setError("An account already exists for this email. Enter your current password to join the household.");
      } else {
        setError(err.message || "Couldn't accept the invite.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
    <div className="auth">
      {/* LEFT — hero */}
      <div className="auth__hero">
        <div className="auth__brand">
          <Logo size={36} />
          <div className="auth__brand-name">
            Community Compass
            <small>HOA · bylaw · clarity</small>
          </div>
        </div>

        <div className="auth__hero-body">
          <h1 className="auth__hero-title">
            Bylaws made <em>simple</em>.<br />
            Community made <em>closer</em>.
          </h1>
          <p className="auth__hero-lede">
            Ask <em>"can I rent my unit this summer?"</em> and get an answer cited
            line-by-line to your HOA's actual rules. Then stick around — sell the
            old grill, find a babysitter, get help moving the couch.
          </p>

          <div className="auth__hero-feats">
            <div className="auth__hero-feat">
              <Icon name="shield" />
              <div><b>Bylaws, decoded.</b> Plain-English answers that cite the
              section, page, and exact quote — never generic HOA advice. Upload
              a revision and every answer the next day reflects it.</div>
            </div>
            <div className="auth__hero-feat">
              <Icon name="users" />
              <div><b>A community that actually feels like one.</b> A neighbors-only
              marketplace, a services board (babysitting, handyman, lawn care),
              and an assistance section for the things you'd normally ask a
              neighbor for help with.</div>
            </div>
            <div className="auth__hero-feat">
              <Icon name="chat" />
              <div><b>Everything stays in sync.</b> Private messages, a shared
              calendar, and a notification bell so nothing important slips —
              no extra Facebook group required.</div>
            </div>
          </div>

          {/* Marketing pricing CTA — web only. The native mobile app shows
              no pricing/purchase CTA and no external purchase link, per
              Apple guideline 3.1.3 (anti-steering). */}
          {onSeePricing && !_isCapacitorNative() && (
            <div className="auth__hero-pricing">
              <button
                type="button"
                className="auth__hero-pricing-link"
                onClick={onSeePricing}
              >
                See pricing & plans <span aria-hidden="true">→</span>
              </button>
              <span className="auth__hero-pricing-sub">
                Plans start at $39/mo · Residents always free
              </span>
            </div>
          )}
        </div>
      </div>

      {/* RIGHT — form panel */}
      <div className="auth__panel">
        <div className="auth__card">
          {sessionExpired && tab === "login" && (
            <div role="status" aria-live="polite" style={{
              marginBottom: 14, padding: "10px 12px",
              background: "var(--secondary-soft)",
              color: "var(--secondary)",
              border: "1px solid color-mix(in oklab, var(--secondary) 12%, transparent)",
              borderRadius: 8, fontSize: 13.5,
              display: "flex", alignItems: "center", gap: 12,
            }}>
              <span style={{ flex: 1 }}>Your session expired. Please sign in again.</span>
              <button
                type="button"
                onClick={() => onSessionExpiredAck && onSessionExpiredAck()}
                aria-label="Dismiss"
                style={{
                  background: "none", border: "none", color: "inherit",
                  fontSize: 18, lineHeight: 1, padding: "0 4px", cursor: "pointer",
                }}
              >
                ×
              </button>
            </div>
          )}
          <h2 className="auth__title">
            {tab === "login" && <>Welcome back.</>}
            {tab === "community" && <>Onboard your HOA.</>}
            {tab === "homeowner" && <>Join your community.</>}
            {tab === "forgot" && <>Reset your password.</>}
            {tab === "forgot-sent" && <>Check your email.</>}
            {tab === "reset" && <>Set a new password.</>}
            {tab === "reset-done" && <>All set.</>}
            {tab === "accept-invite" && <>Join your household.</>}
            {tab === "verify-sent" && <>Confirm your email.</>}
            {tab === "verify-result" && (
              verifyResult === "success" ? <>Email verified.</>
              : verifyResult === "expired" ? <>Link expired.</>
              : verifyResult === "error" ? <>Verification failed.</>
              : <>Verifying…</>
            )}
          </h2>
          <p className="auth__lede">
            {tab === "login" && "Sign in to ask questions, upload bylaws, or review usage."}
            {tab === "community" && "Set up Community Compass for an HOA. You'll be the first manager."}
            {tab === "homeowner" && "Already approved by your HOA? Create your homeowner account."}
            {tab === "forgot" && "Enter the email tied to your account and we'll send you a reset link."}
            {tab === "forgot-sent" && "We sent a reset link to your inbox. It expires in 1 hour."}
            {tab === "reset" && `Pick a new password. ${PASSWORD_HINT}`}
            {tab === "reset-done" && "Your password is updated. You can sign in with it now."}
            {tab === "accept-invite" && "You've been invited to a household on Community Compass."}
            {tab === "verify-sent" && (
              verifyEmailAddress
                ? `We sent a verification link to ${verifyEmailAddress}. Click it to finish setting up your account.`
                : "We sent a verification link to your inbox. Click it to finish setting up your account."
            )}
            {tab === "verify-result" && (
              verifyResult === "success" ? "Your email is confirmed. Sign in to get started."
              : verifyResult === "expired" ? "This verification link is invalid, expired, or already used. Sign in or request a new one."
              : verifyResult === "error" ? "Something went wrong while verifying. Try the link again or request a new one."
              : "Hold on a moment…"
            )}
          </p>

          {(tab === "login" || tab === "community" || tab === "homeowner") && (
            <div className="auth__tabs">
              <button className={tab === "login" ? "is-active" : ""} onClick={() => switchTab("login")}>Sign in</button>
              <button className={tab === "homeowner" ? "is-active" : ""} onClick={() => switchTab("homeowner")}>Homeowner sign-up</button>
              {/* HOA onboarding is a multi-step manager flow we keep
                  desktop-only; mobile users are virtually always
                  residents, not someone provisioning a new HOA. */}
              {!_isCapacitorNative() && (
                <button className={tab === "community" ? "is-active" : ""} onClick={() => switchTab("community")}>Onboard an HOA</button>
              )}
            </div>
          )}

          {error && (
            <div role="alert" style={{
              marginTop: 10, padding: "10px 12px",
              background: "var(--danger-soft, #fdecea)",
              color: "var(--danger, #b3261e)",
              border: "1px solid var(--danger, #b3261e)",
              borderRadius: 8, fontSize: 14,
            }}>
              {error}
            </div>
          )}

          {tab === "login" && (
            <form className="auth__form" onSubmit={submitLogin}>
              {window.OAuthProviderButtons && <window.OAuthProviderButtons mode="login" />}
              <AppStoreBadge />
              <div className="auth__divider" style={{
                display: "flex", alignItems: "center", gap: 12,
                fontSize: 12, color: "var(--muted, #6a6a6a)",
                margin: "8px 0 4px",
              }}>
                <span style={{ flex: 1, height: 1, background: "currentColor", opacity: 0.2 }} />
                <span>or use email</span>
                <span style={{ flex: 1, height: 1, background: "currentColor", opacity: 0.2 }} />
              </div>
              <Field label="Email">
                <input
                  className="input" type="email" autoComplete="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </Field>
              <Field label="Password" hint={
                <a href="#" onClick={(e) => {
                  e.preventDefault();
                  setForgotEmail(loginEmail);  // preload if they typed it already
                  switchTab("forgot");
                }}>Forgot password?</a>
              }>
                <input
                  className="input" type="password" autoComplete="current-password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                />
              </Field>
              <button type="submit" className="btn btn--primary btn--block btn--lg" disabled={busy}>
                {busy ? "Signing in…" : "Sign in"}
              </button>
              <div className="auth__foot">
                New here? <a href="#" onClick={(e) => { e.preventDefault(); switchTab("homeowner"); }}>Create a homeowner account</a>
              </div>
            </form>
          )}

          {tab === "homeowner" && (
            <form className="auth__form" onSubmit={submitHomeowner}>
              {window.OAuthProviderButtons && <window.OAuthProviderButtons mode="login" />}
              <AppStoreBadge />
              <div className="auth__divider" style={{
                display: "flex", alignItems: "center", gap: 12,
                fontSize: 12, color: "var(--muted, #6a6a6a)",
                margin: "8px 0 4px",
              }}>
                <span style={{ flex: 1, height: 1, background: "currentColor", opacity: 0.2 }} />
                <span>or sign up with email</span>
                <span style={{ flex: 1, height: 1, background: "currentColor", opacity: 0.2 }} />
              </div>
              <Field label="State">
                <select className="select" value={hoState} onChange={(e) => setHoState(e.target.value)}>
                  <option value="">Select your state…</option>
                  {US_STATES.map(([code, name]) => (
                    <option key={code} value={code}>{name}</option>
                  ))}
                </select>
              </Field>
              {!hoUseManual ? (
                <Field
                  label="Your community"
                  hint={
                    hoState ? (
                      hoCommunities.list.length > 0
                        ? <a href="#" onClick={(e) => { e.preventDefault(); setHoUseManual(true); }}>Don't see your community? Enter slug manually</a>
                        : (hoCommunities.loading ? "Loading…" : "No communities in this state yet.")
                    ) : "Pick a state first."
                  }
                >
                  <select
                    className="select"
                    value={hoCommunitySlug}
                    onChange={(e) => setHoCommunitySlug(e.target.value)}
                    disabled={!hoState || hoCommunities.loading}
                  >
                    <option value="">{hoState ? "Choose your community…" : "—"}</option>
                    {hoCommunities.list.map((c) => (
                      <option key={c.slug} value={c.slug}>{c.name}</option>
                    ))}
                  </select>
                </Field>
              ) : (
                <Field
                  label="Community slug"
                  hint={<a href="#" onClick={(e) => { e.preventDefault(); setHoUseManual(false); }}>Back to community list</a>}
                >
                  <input
                    className="input mono"
                    value={hoCommunityManual}
                    onChange={(e) => setHoCommunityManual(e.target.value)}
                    placeholder="e.g. cedar-grove"
                  />
                </Field>
              )}
              <div className="auth__form-row">
                <Field label="First name"><input className="input" value={hoFirst} onChange={(e) => setHoFirst(e.target.value)} /></Field>
                <Field label="Last name"><input className="input" value={hoLast} onChange={(e) => setHoLast(e.target.value)} /></Field>
              </div>
              <Field label="Email">
                <input className="input" type="email" value={hoEmail} onChange={(e) => setHoEmail(e.target.value)} />
              </Field>
              <Field label="Unit / address" hint="Used to verify with your community manager.">
                <input className="input" value={hoUnit} onChange={(e) => setHoUnit(e.target.value)} />
              </Field>
              <Field label="Password" hint={PASSWORD_HINT}>
                <input className="input" type="password" value={hoPassword} onChange={(e) => setHoPassword(e.target.value)} />
              </Field>
              <button type="submit" className="btn btn--accent btn--block btn--lg" disabled={busy}>
                {busy ? "Creating account…" : "Create homeowner account"}
              </button>
              <div className="auth__foot">
                Already have an account? <a href="#" onClick={(e) => { e.preventDefault(); switchTab("login"); }}>Sign in</a>
              </div>
            </form>
          )}

          {tab === "community" && (
            <form className="auth__form" onSubmit={submitCommunity}>
              {window.OAuthProviderButtons && <window.OAuthProviderButtons mode="login" />}
              <AppStoreBadge />
              <div className="auth__divider" style={{
                display: "flex", alignItems: "center", gap: 12,
                fontSize: 12, color: "var(--muted, #6a6a6a)",
                margin: "8px 0 4px",
              }}>
                <span style={{ flex: 1, height: 1, background: "currentColor", opacity: 0.2 }} />
                <span>or sign up with email</span>
                <span style={{ flex: 1, height: 1, background: "currentColor", opacity: 0.2 }} />
              </div>
              <Field label="HOA / community name">
                <input className="input" value={cmName} onChange={(e) => setCmName(e.target.value)} placeholder="Cedar Grove" />
              </Field>
              <div className="auth__form-row">
                <Field label="Number of units"><input className="input" type="number" value={cmUnits} onChange={(e) => setCmUnits(e.target.value)} /></Field>
                <Field label="State">
                  <select className="select" value={cmState} onChange={(e) => setCmState(e.target.value)}>
                    <option value="">Select…</option>
                    {US_STATES.map(([code, name]) => (
                      <option key={code} value={code}>{code} — {name}</option>
                    ))}
                  </select>
                </Field>
              </div>
              <Field label="Are you part of a property management company?">
                <select className="select" value={cmCompany} onChange={(e) => setCmCompany(e.target.value)}>
                  <option value="no">No — single HOA account</option>
                  <option value="yes">Yes — already on Community Compass</option>
                  <option value="new">Yes — set up a new company</option>
                </select>
              </Field>
              {cmCompany === "new" && (
                <Field label="Company name" hint="The property management firm — separate from the HOA's name.">
                  <input
                    className="input"
                    value={cmCompanyName}
                    onChange={(e) => setCmCompanyName(e.target.value)}
                    placeholder="e.g. InfraMark Management"
                  />
                </Field>
              )}
              {cmCompany === "yes" && (
                <div className="text-sm text-mute" style={{ marginTop: 4 }}>
                  We'll set you up as a single HOA for now. Reply to your
                  welcome email and we'll attach this HOA to your existing
                  Compass company account.
                </div>
              )}

              <div className="auth__form-row">
                <Field label="Your name"><input className="input" value={cmYourName} onChange={(e) => setCmYourName(e.target.value)} /></Field>
                <Field label="Email"><input className="input" type="email" value={cmEmail} onChange={(e) => setCmEmail(e.target.value)} /></Field>
              </div>
              <Field label="Password" hint={PASSWORD_HINT}>
                <input className="input" type="password" value={cmPassword} onChange={(e) => setCmPassword(e.target.value)} />
              </Field>
              {turnstileSiteKey
                ? <TurnstileWidget siteKey={turnstileSiteKey} onToken={setCmCaptchaToken} />
                : turnstileLoadFailed ? (
                  <div role="alert" style={{
                    padding: "10px 12px", borderRadius: 8, fontSize: 13,
                    background: "var(--danger-soft, #fdecea)",
                    color: "var(--danger, #b3261e)",
                    border: "1px solid var(--danger, #b3261e)",
                    marginBottom: 8,
                  }}>
                    Couldn't load the CAPTCHA. <a href="#" onClick={(e) => {
                      e.preventDefault();
                      setTurnstileFetchAttempt(turnstileFetchAttempt + 1);
                    }}>Try again</a> or refresh the page.
                  </div>
                ) : turnstileSiteKey === null ? (
                  <div className="text-mute" style={{ fontSize: 13, marginBottom: 8 }}>
                    Loading CAPTCHA…
                  </div>
                ) : null}
              <button
                type="submit"
                className="btn btn--accent btn--block btn--lg"
                disabled={
                  busy
                  // Still loading the site key — don't let the user
                  // submit before we know whether a CAPTCHA is needed.
                  || turnstileSiteKey === null
                  // Site key fetched OK and points at a real Turnstile
                  // deploy — wait for the user to solve the challenge.
                  || (Boolean(turnstileSiteKey) && !cmCaptchaToken)
                }
              >
                {busy ? "Creating workspace…" : "Create HOA workspace · 14-day trial"}
              </button>
              <div className="auth__foot">
                Trial includes 250K tokens. No card required.
              </div>
            </form>
          )}

          {tab === "forgot" && (
            <form className="auth__form" onSubmit={submitForgot}>
              <Field label="Email">
                <input
                  className="input" type="email" autoComplete="email"
                  autoFocus
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </Field>
              <button type="submit" className="btn btn--primary btn--block btn--lg" disabled={busy}>
                {busy ? "Sending…" : "Send reset link"}
              </button>
              <div className="auth__foot">
                Remembered it? <a href="#" onClick={(e) => { e.preventDefault(); switchTab("login"); }}>Back to sign in</a>
              </div>
            </form>
          )}

          {tab === "forgot-sent" && (
            <div className="auth__form">
              <div className="card" style={{
                padding: 16, background: "var(--accent-soft, #eef3e6)",
                color: "var(--accent-ink, #3d4f2a)",
                border: "1px solid color-mix(in oklab, var(--accent, #7c8a6b) 20%, transparent)",
                borderRadius: 10, fontSize: 14, lineHeight: 1.5,
              }}>
                If an account exists for <b>{forgotEmail}</b>, a password-reset link
                is on its way. The link expires in 1 hour.
                <div style={{ marginTop: 10, fontSize: 13, color: "var(--mute)" }}>
                  Don't see it? Check your spam folder, or wait a minute and try again.
                </div>
              </div>
              <button
                type="button"
                className="btn btn--ghost btn--block"
                onClick={() => switchTab("login")}
                style={{ marginTop: 14 }}
              >
                Back to sign in
              </button>
              <div className="auth__foot">
                Wrong email? <a href="#" onClick={(e) => { e.preventDefault(); switchTab("forgot"); }}>Try a different one</a>
              </div>
            </div>
          )}

          {tab === "verify-sent" && (
            <div className="auth__form">
              <div className="card" style={{
                padding: 16, background: "var(--accent-soft, #eef3e6)",
                color: "var(--accent-ink, #3d4f2a)",
                border: "1px solid color-mix(in oklab, var(--accent, #7c8a6b) 20%, transparent)",
                borderRadius: 10, fontSize: 14, lineHeight: 1.5,
              }}>
                {verifyEmailAddress ? (
                  <>A verification link is on its way to <b>{verifyEmailAddress}</b>. Click it to finish setting up your account.</>
                ) : (
                  <>A verification link is on its way. Click it to finish setting up your account.</>
                )}
                <div style={{ marginTop: 10, fontSize: 13, color: "var(--mute)" }}>
                  Don't see it? Check your spam folder, or resend below.
                </div>
              </div>
              <button
                type="button"
                className="btn btn--ghost btn--block"
                disabled={busy || !verifyEmailAddress}
                onClick={async () => {
                  if (!verifyEmailAddress) return;
                  setBusy(true);
                  setError("");
                  try {
                    await Api.verifyEmailResend({ email: verifyEmailAddress });
                  } catch (err) {
                    // Endpoint is rate-limited (3/hr) and unconditional 200, so
                    // a real failure here means the network call itself
                    // didn't reach the backend.
                    setError(err.message || "Couldn't resend right now. Try again in a moment.");
                  } finally {
                    setBusy(false);
                  }
                }}
                style={{ marginTop: 14 }}
              >
                {busy ? "Resending…" : "Resend verification email"}
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--block"
                onClick={() => switchTab("login")}
                style={{ marginTop: 8 }}
              >
                Back to sign in
              </button>
            </div>
          )}

          {tab === "verify-result" && (
            <div className="auth__form">
              {verifyResult === "success" && (
                <button
                  type="button"
                  className="btn btn--primary btn--block btn--lg"
                  onClick={() => switchTab("login")}
                >
                  Sign in
                </button>
              )}
              {verifyResult === "expired" && (
                <>
                  <div className="card" style={{
                    padding: 16, background: "var(--accent-soft, #eef3e6)",
                    color: "var(--accent-ink, #3d4f2a)",
                    border: "1px solid color-mix(in oklab, var(--accent, #7c8a6b) 20%, transparent)",
                    borderRadius: 10, fontSize: 14, lineHeight: 1.5,
                  }}>
                    Sign in below; if your account isn't verified yet you'll be
                    shown a "Resend verification email" option.
                  </div>
                  <button
                    type="button"
                    className="btn btn--primary btn--block btn--lg"
                    onClick={() => switchTab("login")}
                    style={{ marginTop: 14 }}
                  >
                    Back to sign in
                  </button>
                </>
              )}
              {verifyResult === "error" && (
                <button
                  type="button"
                  className="btn btn--ghost btn--block"
                  onClick={() => switchTab("login")}
                >
                  Back to sign in
                </button>
              )}
            </div>
          )}

          {tab === "reset" && (
            <form className="auth__form" onSubmit={submitReset}>
              <Field label="New password" hint={PASSWORD_HINT}>
                <input
                  className="input" type="password" autoComplete="new-password"
                  autoFocus
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                />
              </Field>
              <Field label="Confirm new password">
                <input
                  className="input" type="password" autoComplete="new-password"
                  value={resetPasswordConfirm}
                  onChange={(e) => setResetPasswordConfirm(e.target.value)}
                />
              </Field>
              <button type="submit" className="btn btn--primary btn--block btn--lg" disabled={busy}>
                {busy ? "Saving…" : "Set new password"}
              </button>
              <div className="auth__foot">
                Link expired? <a href="#" onClick={(e) => { e.preventDefault(); switchTab("forgot"); }}>Request a new one</a>
              </div>
            </form>
          )}

          {tab === "reset-done" && (
            <div className="auth__form">
              <div className="card" style={{
                padding: 16, background: "var(--accent-soft, #eef3e6)",
                color: "var(--accent-ink, #3d4f2a)",
                border: "1px solid color-mix(in oklab, var(--accent, #7c8a6b) 20%, transparent)",
                borderRadius: 10, fontSize: 14, lineHeight: 1.5,
              }}>
                Your password has been updated. You can now sign in with the new one.
              </div>
              <button
                type="button"
                className="btn btn--primary btn--block btn--lg"
                onClick={() => switchTab("login")}
                style={{ marginTop: 14 }}
              >
                Go to sign in
              </button>
            </div>
          )}

          {tab === "accept-invite" && (
            <form className="auth__form" onSubmit={submitInviteAccept}>
              {!inviteToken ? (
                <div className="card" style={{
                  padding: 16, background: "var(--danger-soft, #fdecea)",
                  color: "var(--danger, #b3261e)", borderRadius: 10, fontSize: 14,
                }}>
                  This page expects an invite link. Open the link your household primary
                  sent you, or ask them to re-send it from Settings → Household.
                </div>
              ) : (
                <>
                  <div className="card" style={{
                    padding: 14, marginBottom: 4,
                    background: "var(--accent-soft, #eef3e6)",
                    color: "var(--accent-ink, #3d4f2a)",
                    borderRadius: 10, fontSize: 14, lineHeight: 1.5,
                  }}>
                    You've been invited to join a household on Community Compass.
                    You'll be added as a <b>family member</b> — full access to events,
                    directory, services, assistance, and messages. AI features
                    (chat + Project Builder) stay with the household primary.
                  </div>

                  <Field label="Your name (optional)">
                    <input
                      className="input" type="text"
                      value={inviteFullName}
                      onChange={(e) => setInviteFullName(e.target.value)}
                      placeholder="So neighbors know who you are"
                    />
                  </Field>

                  <div className="row" style={{ gap: 6, marginTop: 4 }}>
                    <button
                      type="button"
                      className={!inviteHasAccount ? "btn btn--primary btn--sm" : "btn btn--ghost btn--sm"}
                      onClick={() => setInviteHasAccount(false)}
                    >
                      Create new account
                    </button>
                    <button
                      type="button"
                      className={inviteHasAccount ? "btn btn--primary btn--sm" : "btn btn--ghost btn--sm"}
                      onClick={() => setInviteHasAccount(true)}
                    >
                      I already have an account
                    </button>
                  </div>

                  {!inviteHasAccount ? (
                    <Field label="Password" hint={PASSWORD_HINT}>
                      <input
                        className="input" type="password" autoComplete="new-password"
                        value={invitePassword}
                        onChange={(e) => setInvitePassword(e.target.value)}
                      />
                    </Field>
                  ) : (
                    <Field label="Your current Compass password" hint="So a stolen invite link can't take over an account.">
                      <input
                        className="input" type="password" autoComplete="current-password"
                        value={inviteExistingPassword}
                        onChange={(e) => setInviteExistingPassword(e.target.value)}
                      />
                    </Field>
                  )}

                  <button type="submit" className="btn btn--accent btn--block btn--lg" disabled={busy}>
                    {busy ? "Joining…" : "Join household"}
                  </button>
                </>
              )}
              <div className="auth__foot">
                Want to sign in normally instead? <a href="#" onClick={(e) => { e.preventDefault(); switchTab("login"); }}>Go to sign in</a>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>

    {pickerCommunities && (
      <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="community-picker-title">
        <div className="modal">
          <h2 id="community-picker-title" className="auth__title" style={{ marginTop: 0 }}>
            Please select your community
          </h2>
          <p className="auth__lede">
            You're a member of more than one community. Choose the one you'd like to sign in to.
          </p>
          {error && (
            <div role="alert" style={{
              marginTop: 10, padding: "10px 12px",
              background: "var(--danger-soft, #fdecea)",
              color: "var(--danger, #b3261e)",
              border: "1px solid var(--danger, #b3261e)",
              borderRadius: 8, fontSize: 14,
            }}>
              {error}
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 16 }}>
            {pickerCommunities.map((c) => (
              <button
                key={c.id}
                type="button"
                className="btn btn--block"
                disabled={busy}
                onClick={() => pickCommunity(c.id)}
                style={{ justifyContent: "space-between", textAlign: "left" }}
              >
                <span>{c.name}</span>
                {c.state && <span className="text-mute mono" style={{ fontSize: 12 }}>{c.state}</span>}
              </button>
            ))}
          </div>
          <div className="auth__foot" style={{ marginTop: 16 }}>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                if (busy) return;
                setPickerCommunities(null);
                setError("");
              }}
            >
              Cancel
            </a>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

export { AuthScreen };
